import * as Sentry from '@sentry/react';

import {sentryDns, releaseName, environment} from '~/shared/config';

import {remoteLoggingIgnoreList, sentryLogIgnoreList} from './consoleIgnoreList';

const NETWORK_ERROR = 'Network Error';

// standard ignoreErrors and denyUrls from https://gist.github.com/impressiver/5092952
const standardIgnores = {
  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    'Can\'t find variable: ZiteReader',
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Generic error code from errors outside the security sandbox
    // You can delete this if using raven.js > 1.0, which ignores these automatically.
    'Script error.',
  ],
  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  ],
};

const denyUrls = [
  ...standardIgnores.denyUrls,
  /^https:\/\/dc\.services\.visualstudio\.com/,
  /^https:\/\/bam\..*\.nr-data\.net/,
  /^https:\/\/script\.crazyegg\.com/,
  /^https:\/\/stats\.g\.doubleclick\.net/,
  /^https:\/\/www\.google-analytics\.com/,
  /^https:\/\/.*\.appsflyer\.com/,
  /^https:\/\/.*\.hotjar\.io/,
  /^https:\/\/.*\.hotjar\.com/,
  /^https:\/\/ekr\.zdassets\.com/,
  /^https:\/\/.*\.intercom\.com/,
  /maps\.googleapis\.com/i,
  /chrome-extension:/i,
  /utag/,
];

// App-specific, data-driven ignores. Every entry maps to a high-volume issue confirmed
// in Sentry (justeattakeaway/10bis-next) that is handled, transient, third-party, or
// environmental — i.e. not actionable. Keep this list evidence-based: add an entry only
// after confirming it's noise, and comment WHY + WHERE it comes from so it can be revisited.
const nonActionableErrors = [
  // Web Storage blocked (private mode / in-app webviews) — localStorage/sessionStorage is null.
  // ~41K/14d. Root cause now defended at source via `safeLocalStorage`/`safeSessionStorage`
  // (~/shared/utils/safeStorage); kept as a thin net for any un-migrated/third-party caller.
  /Cannot read properties of null \(reading 'getItem'\)/i,
  /null is not an object \(evaluating '.*\.getItem'\)/i, // Safari phrasing of the same
  // Handled errors thrown/rejected as plain strings/objects (e.g. `throw error.message`)
  // surface as these synthetic wrappers. The user already saw a UI error state. ~17K/14d.
  'Non-Error promise rejection captured',
  'Object captured as promise rejection',
  // User-facing "something went wrong, try again later" — already shown to the user. ~6K/14d.
  'משהו השתבש, נסו שוב מאוחר יותר',
  // Transient connectivity — not actionable per-event; real outages are caught server-side.
  NETWORK_ERROR,
  /timeout of \d+ms exceeded/i,
  /Failed to fetch.*cloudfront/i, // CDN asset fetch blips
  // Stale deploy: a cached index.html references a chunk that no longer exists. ~1.7K/14d.
  // Better long-term: reload the page on ChunkLoadError instead of ignoring it.
  'ChunkLoadError',
  /Loading (CSS )?chunk .* failed/i,
  // Third-party SDK noise.
  /Document is ready and element paypal-button.* does not exist/i, // PayPal SDK
  // In-app webview bridges (native app wrapping the site) — irrelevant to the web app.
  'window.webkit.messageHandlers', // iOS
  'Java object is gone', // Android
];

const ignoreErrors = [...standardIgnores.ignoreErrors, ...nonActionableErrors];

const sentryLogLevelMap = {
  verbose: 'debug',
  log: 'info',
  warn: 'warn',
  error: 'error',
  errorDialog: 'error',
};

export function logStructured({loggerName, logLevel, message, data}) {
  const text = String(message ?? '');

  // Drop high-frequency, already-handled logs before they reach Sentry Logs. This is
  // the hard cap on ingestion (the bulk of Sentry volume came from these messages).
  if (sentryLogIgnoreList.some(pattern => pattern.test(text))) {
    return;
  }

  const level = sentryLogLevelMap[logLevel] || 'info';
  Sentry.logger[level](`[${loggerName}] ${text}`, data ? {data} : undefined);
}

export function init() {
  Sentry.init({
    dsn: sentryDns,
    // Minimal setup: errors only. No tracing and no session replay (both are quota-heavy).
    release: releaseName,
    environment,
    maxBreadcrumbs: 30,
    // Send only half of error events to keep volume low.
    sampleRate: 0.5,
    _experiments: {
      enableLogs: false,
    },
    beforeBreadcrumb(event, hint) {
      const ignoreEvent =
        (hint?.xhr && denyUrls.some(i => i.test(event.data?.url || hint.xhr.responseURL))) ||
        (event?.message && remoteLoggingIgnoreList.some(i => i.test(event.message)));

      if (ignoreEvent) {
        return null;
      }

      return event;
    },
    ignoreErrors,
    denyUrls,
  });
}

export function log({loggerName, logLevel, errorOrMessage, args}) {
  Sentry.addBreadcrumb({
    category: loggerName,
    message: (errorOrMessage || '').toString(),
    level: logLevel,
    data: {
      args: JSON.stringify(args),
    },
  });
}

function shouldIgnoreError(args) {
  if (args?.[0]?.message?.includes(NETWORK_ERROR)) {
    return true;
  }
  // GetOrderSuccess returns a user-facing Hebrew error that is not actionable
  const isGetOrderSuccessError = ctx => ctx?.url?.includes('GetOrderSuccess') && ctx?.message?.includes('משהו השתבש');
  return isGetOrderSuccessError(args?.[0]) || isGetOrderSuccessError(args?.[0]?.e);
}

export function logError({error: rawError, loggerName, args}) {
  if (shouldIgnoreError(args)) {
    return;
  }

  log({errorOrMessage: rawError, loggerName, logLevel: 'error', args});
  Sentry.withScope(scope => {
    if ('fingerprint' in (args?.[0] || {})) {
      scope.setFingerprint(args[0].fingerprint);
    }
    Sentry.captureException(rawError);
  });
}
